import { StatusCodes } from 'http-status-codes'

import { ResidentAppErrorCode } from '../types'

const errSpec = {
  redirect: StatusCodes.SEE_OTHER, // 303
  validationError: StatusCodes.BAD_REQUEST, // 400
  badRequest: StatusCodes.BAD_REQUEST, // 400
  authorization: StatusCodes.UNAUTHORIZED, // 401
  notFound: StatusCodes.NOT_FOUND, // 404
  conflict: StatusCodes.CONFLICT, // 409
  internalError: StatusCodes.INTERNAL_SERVER_ERROR, // 500
  serviceUnavailable: StatusCodes.SERVICE_UNAVAILABLE, // 503
  clientError: -1, // Used by frontend only
} as const

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ErrorDetails = any
type ErrorType = keyof typeof errSpec
type ErrorCode = `${ResidentAppErrorCode}`
type OriginatingError = unknown

export class ResidentAppError extends Error {
  readonly errType: ErrorType

  readonly errorCode: ErrorCode

  readonly errorDetails?: ErrorDetails

  readonly originatingError?: OriginatingError

  private constructor(
    errType: keyof typeof errSpec,
    errorCode: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ) {
    super(opts?.message)
    this.errType = errType
    this.errorCode = errorCode
    this.originatingError = opts?.originatingError
    this.errorDetails = opts?.errorDetails
  }

  httpCode() {
    return errSpec[this.errType]
  }

  /**
   * This method is used to create an *VALIDATION_ERROR* error with a *400* status code
   */
  public static Validation(opts?: {
    message: string
    originatingError: OriginatingError
  }): ResidentAppError {
    return new ResidentAppError('validationError', 'VALIDATION_ERROR', opts)
  }

  /**
   * This method is used to create an error with a *404* status code
   */
  public static NotFound(
    error: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    return new ResidentAppError('notFound', error, opts)
  }

  /**
   * This method is used to create an error with a *401* status code
   */
  public static Authorization(
    error: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    return new ResidentAppError('authorization', error, opts)
  }

  /**
   * This method is used to create an error with a *500* status code
   */
  public static InternalError(
    error: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    return new ResidentAppError('internalError', error, opts)
  }

  /**
   * This method is used to create an error with a *503* status code
   */
  public static ServiceUnavailable(
    error: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    return new ResidentAppError('serviceUnavailable', error, opts)
  }

  /**
   * This method is used to create an error with a *400* status code
   */
  public static BadRequest(
    error: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    return new ResidentAppError('badRequest', error, opts)
  }

  /**
   * This method is used to create an error with a *400* status code
   */
  public static Redirect(
    redirectUrl: string,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    return new ResidentAppError('redirect', 'REDIRECT', {
      message: opts?.message,
      originatingError: opts?.originatingError,
      errorDetails: {
        redirectUrl,
        ...opts?.errorDetails,
      },
    })
  }

  /**
   * This method is used to create an error from the fetchWrappers response
   */
  public static FromResponse(
    statusCode: number,
    errorJson: unknown,
    errorDetails?: ErrorDetails
  ): ResidentAppError {
    const entry = Object.entries(errSpec).find((item) => item[1] === statusCode)
    const errType = (entry?.[0] || 'internalError') as ErrorType

    if (statusCode === StatusCodes.BAD_REQUEST) {
      return new ResidentAppError('badRequest', 'BAD_REQUEST', {
        message: 'Bad request',
        errorDetails: {
          statusCode,
          response: errorJson,
        },
      })
    }

    if (statusCode === StatusCodes.NOT_FOUND) {
      return new ResidentAppError('notFound', 'NOT_FOUND', {
        message: 'Resource not found',
        errorDetails: {
          statusCode,
          response: errorJson,
        },
      })
    }

    if (statusCode === StatusCodes.CONFLICT) {
      return new ResidentAppError('conflict', 'CONFLICT', {
        message: 'Request conflicts with current state',
        errorDetails: {
          statusCode,
          response: errorJson,
        },
      })
    }

    if (statusCode === StatusCodes.UNAUTHORIZED) {
      return new ResidentAppError('authorization', 'UNAUTHORIZED', {
        errorDetails: {
          statusCode,
          response: errorJson,
        },
      })
    }

    return new ResidentAppError(errType, 'INTERNAL_SERVER_ERROR', {
      message: 'Core API returned an error',
      errorDetails: {
        ...errorDetails,
        statusCode,
        response: errorJson,
      },
    })
  }

  /**
   * This method is used to create an error by the frontend
   */
  public static ClientError(
    error: ErrorCode,
    opts?: {
      message?: string
      originatingError?: OriginatingError
      errorDetails?: ErrorDetails
    }
  ): ResidentAppError {
    // eslint-disable-next-line no-console
    console.error('ClientError:', error, opts)

    return new ResidentAppError('badRequest', error, opts)
  }
}
